// Serviços gratuitos usados:
//   STAC API: https://earth-search.aws.element84.com/v1  (sem chave)
//   TiTiler:  https://titiler.xyz                        (instância pública, sem chave)

const STAC_API = 'https://earth-search.aws.element84.com/v1';
const TITILER_API = 'https://titiler.xyz';

/**
 * Busca cenas Sentinel-2 L2A para um bounding box e período.
 * @param {number[]} bbox       [west, south, east, north]
 * @param {string}   dataInicio 'YYYY-MM-DD'
 * @param {string}   dataFim    'YYYY-MM-DD'
 * @param {number}   maxNuvens  cobertura máxima de nuvens em %
 * @returns {Promise<object[]>} array de features STAC
 */
export async function buscarCenas(bbox, dataInicio, dataFim, maxNuvens = 20) {
  const res = await fetch(`${STAC_API}/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      collections: ['sentinel-2-l2a'],
      bbox,
      datetime: `${dataInicio}T00:00:00Z/${dataFim}T23:59:59Z`,
      query: { 'eo:cloud_cover': { lt: maxNuvens } },
      limit: 12,
      sortby: [{ field: 'properties.datetime', direction: 'desc' }],
    }),
  });
  if (!res.ok) throw new Error(`STAC error ${res.status}`);
  const data = await res.json();
  return data.features || [];
}

/**
 * Monta a URL de tiles TiTiler para ser usada no Leaflet TileLayer.
 * @param {object} feature  Feature STAC completo
 * @param {'rgb'|'cir'|'ndvi'} tipo
 */
export function getTileUrl(feature, tipo) {
  const stacUrl = getStacSelfUrl(feature);

  if (tipo === 'rgb') {
    // Usa o asset 'visual' (TCI — True Color Image, RGB pré-composto uint8)
    // via endpoint /cog/ — mais simples e confiável que o endpoint /stac/
    const visualHref = feature?.assets?.visual?.href;
    if (visualHref) {
      const p = new URLSearchParams({ url: visualHref, rescale: '1,255' });
      return `${TITILER_API}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}?${p.toString()}`;
    }
    // Fallback: STAC endpoint com bandas individuais
    const p = new URLSearchParams({ url: stacUrl });
    p.append('assets', 'red');
    p.append('assets', 'green');
    p.append('assets', 'blue');
    p.set('rescale', '0,3000');
    return `${TITILER_API}/stac/tiles/WebMercatorQuad/{z}/{x}/{y}?${p.toString()}`;
  }

  // CIR e NDVI: endpoint STAC com expressão de bandas
  const p = new URLSearchParams({ url: stacUrl });
  if (tipo === 'cir') {
    // Color Infrared — vegetação saudável aparece em vermelho intenso
    p.append('assets', 'nir');
    p.append('assets', 'red');
    p.append('assets', 'green');
    p.set('rescale', '0,5000');
  } else if (tipo === 'ndvi') {
    // NDVI real: (NIR - Red) / (NIR + Red) — valores de -1 a +1
    p.append('assets', 'nir');
    p.append('assets', 'red');
    p.set('expression', '(b1-b2)/(b1+b2)');
    p.set('colormap_name', 'rdylgn');
    p.set('rescale', '-1,1');
  }
  return `${TITILER_API}/stac/tiles/WebMercatorQuad/{z}/{x}/{y}?${p.toString()}`;
}

// ── Tile stitching para relatório PDF ────────────────────────────────────────

function lngLatToTileCoords(lng, lat, z) {
  const n = 2 ** z;
  const x = Math.floor((lng + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return { x, y };
}

function adaptiveZoom(bbox) {
  const [W, S, E, N] = bbox;
  // Cap em 14: overviews acima disso podem não existir em cenas recentes,
  // causando geração on-the-fly lenta/timeout no TiTiler.
  for (let z = 14; z >= 8; z--) {
    const tl = lngLatToTileCoords(W, N, z);
    const br = lngLatToTileCoords(E, S, z);
    if ((br.x - tl.x + 1) <= 3 && (br.y - tl.y + 1) <= 3) return z;
  }
  return 8;
}

/**
 * Monta uma imagem PNG focada na área (bbox) para relatório PDF,
 * unindo os tiles TiTiler correspondentes ao polígono.
 * Usa os mesmos endpoints que o Leaflet — garante que funciona.
 * @param {object}   feature  Feature STAC completo
 * @param {'rgb'|'cir'|'ndvi'} tipo
 * @param {number[]} bbox     [west, south, east, north]
 * @returns {Promise<string|null>} data URL PNG ou null se falhar
 */
export async function stitchTilesForArea(feature, tipo, bbox) {
  const zoom = adaptiveZoom(bbox);
  const tileUrlTemplate = getTileUrl(feature, tipo);
  const [W, S, E, N] = bbox;
  const tl = lngLatToTileCoords(W, N, zoom);
  const br = lngLatToTileCoords(E, S, zoom);
  const cols = br.x - tl.x + 1;
  const rows = br.y - tl.y + 1;

  const blobToDataUrl = (blob) => new Promise(r => { const fr = new FileReader(); fr.onloadend = () => r(fr.result); fr.readAsDataURL(blob); });

  // Busca tile com até 3 tentativas (TiTiler público pode ser lento na geração on-the-fly)
  const fetchAsDataUrl = async (url) => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 3000 * attempt));
        const res = await fetch(url);
        if (!res.ok) { console.warn('[Sensoriamento] tile HTTP', res.status, url); continue; }
        return await blobToDataUrl(await res.blob());
      } catch (err) {
        console.warn('[Sensoriamento] tile fetch error (tentativa', attempt + 1, ')', err?.message, url);
      }
    }
    return null;
  };

  // Busca tiles em batches de 3 para não sobrecarregar o TiTiler público
  const BATCH_SIZE = 3;
  const allTiles = Array.from({ length: rows }, (_, ri) =>
    Array.from({ length: cols }, (_, ci) => ({ tx: tl.x + ci, ty: tl.y + ri, ci, ri }))
  ).flat();
  const tileData = [];
  for (let b = 0; b < allTiles.length; b += BATCH_SIZE) {
    const batch = allTiles.slice(b, b + BATCH_SIZE);
    const results = await Promise.all(batch.map(({ tx, ty, ci, ri }) => {
      const url = tileUrlTemplate.replace('{z}', zoom).replace('{x}', tx).replace('{y}', ty);
      return fetchAsDataUrl(url).then(dataUrl => ({ dataUrl, ci, ri }));
    }));
    tileData.push(...results);
    if (b + BATCH_SIZE < allTiles.length) await new Promise(r => setTimeout(r, 400));
  }

  if (tileData.every(t => !t.dataUrl)) return null;

  // Monta canvas com todos os tiles
  const canvas = document.createElement('canvas');
  canvas.width = cols * 256;
  canvas.height = rows * 256;
  const ctx = canvas.getContext('2d');

  for (const { dataUrl, ci, ri } of tileData) {
    if (!dataUrl) continue;
    await new Promise(r => {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, ci * 256, ri * 256, 256, 256); r(); };
      img.onerror = r;
      img.src = dataUrl;
    });
  }

  // Recorta o canvas exatamente no bbox do polígono (sem mostrar tiles vizinhos)
  const n2 = 2 ** zoom;
  const lngToFracX = (lng) => (lng + 180) / 360 * n2;
  const latToFracY = (lat) => {
    const latRad = lat * Math.PI / 180;
    return (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n2;
  };
  const px0 = Math.round((lngToFracX(W) - tl.x) * 256);
  const py0 = Math.round((latToFracY(N) - tl.y) * 256);
  const px1 = Math.round((lngToFracX(E) - tl.x) * 256);
  const py1 = Math.round((latToFracY(S) - tl.y) * 256);
  const cropW = Math.max(px1 - px0, 1);
  const cropH = Math.max(py1 - py0, 1);

  const cropped = document.createElement('canvas');
  cropped.width = cropW;
  cropped.height = cropH;
  cropped.getContext('2d').drawImage(canvas, px0, py0, cropW, cropH, 0, 0, cropW, cropH);

  return cropped.toDataURL('image/png');
}

/** Extrai a URL self de um item STAC. */
export function getStacSelfUrl(feature) {
  const self = feature.links?.find(l => l.rel === 'self');
  return self?.href ?? `${STAC_API}/collections/sentinel-2-l2a/items/${feature.id}`;
}

/** Retorna o instante da cena STAC ou NaN quando a data não é válida. */
export function getCenaTimestamp(feature) {
  return Date.parse(feature?.properties?.datetime || '');
}

/**
 * Encontra a cena imediatamente anterior à selecionada.
 * A ordem recebida não importa e cenas com data inválida são ignoradas.
 */
export function encontrarCenaAnterior(cenas, cenaAtual) {
  const instanteAtual = getCenaTimestamp(cenaAtual);
  if (!Number.isFinite(instanteAtual)) return null;

  return (cenas || [])
    .filter(cena => cena?.id !== cenaAtual?.id)
    .map(cena => ({ cena, instante: getCenaTimestamp(cena) }))
    .filter(item => Number.isFinite(item.instante) && item.instante < instanteAtual)
    .sort((a, b) => b.instante - a.instante)[0]?.cena || null;
}

/**
 * Compara duas médias NDVI. Mantém a diferença absoluta em pontos de NDVI
 * e calcula a variação relativa sobre o valor absoluto da cena anterior.
 */
export function calcularEvolucaoNdvi(mediaAtual, mediaAnterior) {
  const atual = Number(mediaAtual);
  const anterior = Number(mediaAnterior);
  if (!Number.isFinite(atual) || !Number.isFinite(anterior)) return null;

  const delta = atual - anterior;
  const changePct = Math.abs(anterior) > 0.000001
    ? (delta / Math.abs(anterior)) * 100
    : null;

  return { atual, anterior, delta, changePct };
}

/**
 * Obtém imagem PNG recortada no bbox via TiTiler /cog/bbox/ ou /stac/bbox/.
 * Uma única request por camada — muito mais rápido que tile-stitching.
 * @param {object}   feature  Feature STAC completo
 * @param {'rgb'|'cir'|'ndvi'} tipo
 * @param {number[]} bbox     [west, south, east, north]
 * @returns {Promise<string|null>} data URL PNG ou null se falhar
 */
export async function getPreviewImage(feature, tipo, bbox) {
  const [W, S, E, N] = bbox;
  const bboxPath = `${W},${S},${E},${N}`;
  // Usa apenas width — TiTiler calcula height preservando a proporção real do bbox.
  const SIZE = 512;
  const stacUrl = getStacSelfUrl(feature);

  let url;
  if (tipo === 'rgb') {
    const visualHref = feature?.assets?.visual?.href;
    if (visualHref) {
      const p = new URLSearchParams({ url: visualHref, rescale: '1,255', width: SIZE });
      url = `${TITILER_API}/cog/bbox/${bboxPath}.png?${p}`;
    } else {
      const p = new URLSearchParams({ url: stacUrl, rescale: '0,3000', width: SIZE });
      p.append('assets', 'red'); p.append('assets', 'green'); p.append('assets', 'blue');
      url = `${TITILER_API}/stac/bbox/${bboxPath}.png?${p}`;
    }
  } else if (tipo === 'cir') {
    const p = new URLSearchParams({ url: stacUrl, rescale: '0,5000', width: SIZE });
    p.append('assets', 'nir'); p.append('assets', 'red'); p.append('assets', 'green');
    url = `${TITILER_API}/stac/bbox/${bboxPath}.png?${p}`;
  } else { // ndvi — (NIR - Red) / (NIR + Red), valores -1 a +1
    const p = new URLSearchParams({ url: stacUrl, expression: '(b1-b2)/(b1+b2)', colormap_name: 'rdylgn', rescale: '-1,1', width: SIZE });
    p.append('assets', 'nir'); p.append('assets', 'red');
    url = `${TITILER_API}/stac/bbox/${bboxPath}.png?${p}`;
  }

  const blobToDataUrl = blob => new Promise(r => { const fr = new FileReader(); fr.onloadend = () => r(fr.result); fr.readAsDataURL(blob); });

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      if (attempt > 0) await new Promise(r => setTimeout(r, 2500 * attempt));
      const res = await fetch(url);
      if (!res.ok) { console.warn('[Sensoriamento] preview HTTP', res.status, tipo, url); continue; }
      return await blobToDataUrl(await res.blob());
    } catch (err) {
      console.warn('[Sensoriamento] preview error', tipo, attempt + 1, err?.message);
    }
  }
  return null;
}

/**
 * Teste ponto-em-polígono (ray casting, regra even-odd) usando o anel externo.
 * @param {number} lng
 * @param {number} lat
 * @param {number[][]} anel  array de [lng, lat]
 */
function pontoDentroPoligono(lng, lat, anel) {
  let dentro = false;
  for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
    const [xi, yi] = anel[i];
    const [xj, yj] = anel[j];
    const intersecta = (yi > lat) !== (yj > lat) &&
      lng < (xj - xi) * (lat - yi) / (yj - yi) + xi;
    if (intersecta) dentro = !dentro;
  }
  return dentro;
}

/**
 * Baixa o raster NDVI cru (GeoTIFF, sem colormap) recortado no bbox do polígono
 * e o decodifica no navegador. O bbox de saída é WGS84, então a grade de pixels
 * é linear (permite calcular lng/lat de cada pixel sem reprojeção).
 * @returns {Promise<{raster: Float32Array, width: number, height: number,
 *   bbox: number[], anel: number[][], nodata: number}>}
 */
async function obterRasterNDVI(feature, poligono) {
  // O leitor precisa acompanhar o bundle principal do sensoriamento. Quando
  // ficava em um chunk tardio, um servidor reiniciado ou um cache antigo podia
  // deixar a tela inteira funcionando e falhar somente ao clicar em NDVI.
  const { fromArrayBuffer } = await import(
    /* webpackMode: "eager" */ 'geotiff'
  );

  const geometry = poligono?.type === 'Feature' ? poligono.geometry : poligono;
  const anel = geometry?.coordinates?.[0];
  if (!anel || anel.length < 3) throw new Error('Polígono do talhão inválido');

  const bbox = poligonoParaBbox(anel.map(([lng, lat]) => ({ lng, lat })));
  const [W, S, E, N] = bbox;

  const stacUrl = getStacSelfUrl(feature);
  const SIZE = 256;
  const p = new URLSearchParams({ url: stacUrl, width: SIZE });
  p.append('assets', 'nir');
  p.append('assets', 'red');
  p.set('expression', '(b1-b2)/(b1+b2)');

  const res = await fetch(`${TITILER_API}/stac/bbox/${W},${S},${E},${N}.tif?${p.toString()}`);
  if (!res.ok) throw new Error(`Raster NDVI: HTTP ${res.status}`);

  const buffer = await res.arrayBuffer();
  const tiff = await fromArrayBuffer(buffer);
  const image = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();
  const [raster] = await image.readRasters();
  const nodata = image.getGDALNoData();

  return { raster, width, height, bbox, anel, nodata };
}

function interpolarCor(inicio, fim, proporcao) {
  const t = Math.max(0, Math.min(1, proporcao));
  return [
    Math.round(inicio[0] + (fim[0] - inicio[0]) * t),
    Math.round(inicio[1] + (fim[1] - inicio[1]) * t),
    Math.round(inicio[2] + (fim[2] - inicio[2]) * t),
  ];
}

function corContinuaNdvi(valor) {
  const baixo = hexParaRgb('#d73027');
  const medio = hexParaRgb('#fee08b');
  const alto = hexParaRgb('#1a9850');
  const normalizado = Math.max(0, Math.min(1, (Number(valor) + 1) / 2));
  return normalizado <= 0.5
    ? interpolarCor(baixo, medio, normalizado * 2)
    : interpolarCor(medio, alto, (normalizado - 0.5) * 2);
}

function criarImagemNdviRecortada({ width, height, bbox, anel, obterValor }) {
  const [W, S, E, N] = bbox;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(width, height);

  for (let row = 0; row < height; row++) {
    const lat = N - ((row + 0.5) / height) * (N - S);
    for (let col = 0; col < width; col++) {
      const offset = (row * width + col) * 4;
      const lng = W + ((col + 0.5) / width) * (E - W);
      if (!pontoDentroPoligono(lng, lat, anel)) {
        imageData.data[offset + 3] = 0;
        continue;
      }

      const valor = obterValor(row, col);
      if (!Number.isFinite(valor) || valor < -1 || valor > 1) {
        imageData.data[offset + 3] = 0;
        continue;
      }

      const cor = corContinuaNdvi(valor);
      imageData.data[offset] = cor[0];
      imageData.data[offset + 1] = cor[1];
      imageData.data[offset + 2] = cor[2];
      imageData.data[offset + 3] = 225;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return {
    dataUrl: canvas.toDataURL('image/png'),
    bounds: [[S, W], [N, E]],
  };
}

/**
 * Gera a camada NDVI com transparência total fora do polígono selecionado.
 * O mapa-base permanece visível ao redor do talhão ou da propriedade.
 */
export async function gerarMapaNdviRecortado(feature, poligono) {
  const { raster, width, height, bbox, anel, nodata } = await obterRasterNDVI(feature, poligono);
  return criarImagemNdviRecortada({
    width,
    height,
    bbox,
    anel,
    obterValor: (row, col) => {
      const valor = raster[row * width + col];
      return valor === nodata ? Number.NaN : valor;
    },
  });
}

/** Camada demonstrativa usada somente no preview local protegido. */
export function gerarMapaNdviDemonstrativo(poligono) {
  const geometry = poligono?.type === 'Feature' ? poligono.geometry : poligono;
  const anel = geometry?.coordinates?.[0];
  if (!anel || anel.length < 3) return null;
  const bbox = poligonoParaBbox(anel.map(([lng, lat]) => ({ lng, lat })));
  const width = 320;
  const height = 240;
  return criarImagemNdviRecortada({
    width,
    height,
    bbox,
    anel,
    obterValor: (row, col) => {
      const x = col / Math.max(width - 1, 1);
      const y = row / Math.max(height - 1, 1);
      return Math.max(-1, Math.min(1, 0.48 + (0.24 * x) - (0.12 * y) + (0.1 * Math.sin((x + y) * 12))));
    },
  });
}

/**
 * Calcula estatísticas zonais de NDVI (min, max, média, desvio padrão e histograma)
 * recortadas exatamente pelo polígono do talhão.
 *
 * O TiTiler público (titiler.xyz) não habilita POST /stac/statistics (só GET, sem
 * geometria), então baixamos o raster NDVI cru e computamos tudo no navegador,
 * mascarando os pixels fora do polígono.
 *
 * @param {object} feature   Feature STAC completo (cena selecionada)
 * @param {object} poligono  GeoJSON Feature ou Geometry (Polygon) do talhão
 * @returns {Promise<{min:number,max:number,mean:number,std:number,count:number,
 *   histogram:[number[],number[]]}>}
 */
export async function calcularEstatisticasNDVI(feature, poligono) {
  const { raster, width, height, bbox, anel, nodata } = await obterRasterNDVI(feature, poligono);
  const [W, S, E, N] = bbox;

  // Histograma: 10 bins uniformes entre -1 e 1
  const NUM_BINS = 10;
  const contagens = new Array(NUM_BINS).fill(0);
  const limites = Array.from({ length: NUM_BINS + 1 }, (_, i) => -1 + (2 * i) / NUM_BINS);

  let soma = 0, somaQuad = 0, count = 0;
  let min = Infinity, max = -Infinity;

  for (let row = 0; row < height; row++) {
    const lat = N - ((row + 0.5) / height) * (N - S);
    for (let col = 0; col < width; col++) {
      const valor = raster[row * width + col];
      if (valor === nodata || !Number.isFinite(valor) || valor < -1 || valor > 1) continue;

      const lng = W + ((col + 0.5) / width) * (E - W);
      if (!pontoDentroPoligono(lng, lat, anel)) continue;

      soma += valor;
      somaQuad += valor * valor;
      count++;
      if (valor < min) min = valor;
      if (valor > max) max = valor;

      let bin = Math.floor(((valor + 1) / 2) * NUM_BINS);
      if (bin >= NUM_BINS) bin = NUM_BINS - 1;
      if (bin < 0) bin = 0;
      contagens[bin]++;
    }
  }

  if (count === 0) throw new Error('Nenhum pixel válido dentro do polígono');

  const mean = soma / count;
  const std = Math.sqrt(Math.max(somaQuad / count - mean * mean, 0));

  return { min, max, mean, std, count, histogram: [contagens, limites] };
}

// Cores das zonas de manejo (baixo/médio/alto vigor)
export const CORES_ZONA_MANEJO = { baixo: '#d73027', medio: '#fee08b', alto: '#1a9850' };

/**
 * Gera um mapa espacial de zonas de manejo (baixo/médio/alto vigor) a partir do
 * NDVI recortado pelo polígono do talhão. Classifica cada pixel usando média ± desvio
 * padrão do próprio talhão e desenha o resultado num canvas (transparente fora do
 * polígono/nodata), pronto para uso como ImageOverlay do Leaflet.
 *
 * @param {object} feature   Feature STAC completo (cena selecionada)
 * @param {object} poligono  GeoJSON Feature ou Geometry (Polygon) do talhão
 * @returns {Promise<{dataUrl: string, bounds: [[number,number],[number,number]],
 *   baixoPct: number, medioPct: number, altoPct: number, mean: number, std: number}>}
 */
export async function gerarMapaZonasManejo(feature, poligono) {
  const { raster, width, height, bbox, anel, nodata } = await obterRasterNDVI(feature, poligono);
  const [W, S, E, N] = bbox;

  // 1ª passada: máscara de pixels válidos/dentro do polígono + média e desvio padrão
  const dentro = new Uint8Array(width * height);
  let soma = 0, somaQuad = 0, count = 0;

  for (let row = 0; row < height; row++) {
    const lat = N - ((row + 0.5) / height) * (N - S);
    for (let col = 0; col < width; col++) {
      const idx = row * width + col;
      const valor = raster[idx];
      if (valor === nodata || !Number.isFinite(valor) || valor < -1 || valor > 1) continue;

      const lng = W + ((col + 0.5) / width) * (E - W);
      if (!pontoDentroPoligono(lng, lat, anel)) continue;

      dentro[idx] = 1;
      soma += valor;
      somaQuad += valor * valor;
      count++;
    }
  }
  if (count === 0) throw new Error('Nenhum pixel válido dentro do polígono');

  const mean = soma / count;
  const std = Math.sqrt(Math.max(somaQuad / count - mean * mean, 0));
  const limiarBaixo = mean - std;
  const limiarAlto = mean + std;

  const corBaixo = hexParaRgb(CORES_ZONA_MANEJO.baixo);
  const corMedio = hexParaRgb(CORES_ZONA_MANEJO.medio);
  const corAlto = hexParaRgb(CORES_ZONA_MANEJO.alto);

  // 2ª passada: pinta o canvas classificando cada pixel dentro do polígono
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const imgData = ctx.createImageData(width, height);

  let baixoQtd = 0, medioQtd = 0, altoQtd = 0;

  for (let i = 0; i < width * height; i++) {
    const off = i * 4;
    if (!dentro[i]) { imgData.data[off + 3] = 0; continue; }

    const valor = raster[i];
    let cor;
    if (valor < limiarBaixo) { cor = corBaixo; baixoQtd++; }
    else if (valor > limiarAlto) { cor = corAlto; altoQtd++; }
    else { cor = corMedio; medioQtd++; }

    imgData.data[off] = cor[0];
    imgData.data[off + 1] = cor[1];
    imgData.data[off + 2] = cor[2];
    imgData.data[off + 3] = 190; // ~75% opacidade
  }

  ctx.putImageData(imgData, 0, 0);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    bounds: [[S, W], [N, E]], // formato Leaflet: [[lat,lng],[lat,lng]]
    baixoPct: (baixoQtd / count) * 100,
    medioPct: (medioQtd / count) * 100,
    altoPct: (altoQtd / count) * 100,
    mean,
    std,
  };
}

function hexParaRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Calcula o bounding box a partir de um array de pontos Leaflet {lat, lng}.
 * Retorna [west, south, east, north].
 */
export function poligonoParaBbox(pontos) {
  const lngs = pontos.map(p => p.lng);
  const lats = pontos.map(p => p.lat);
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
}

/**
 * Converte um GeoJSON Feature (Polygon) para posições Leaflet [[lat, lng], ...].
 * GeoJSON usa [lng, lat]; Leaflet usa [lat, lng].
 */
export function geojsonParaLeaflet(geoJsonFeature) {
  return geoJsonFeature.geometry.coordinates[0].map(c => [c[1], c[0]]);
}

/**
 * Converte pontos Leaflet [{lat, lng}] para GeoJSON Feature (Polygon).
 * Fecha o anel automaticamente.
 */
export function leafletParaGeoJson(pontos) {
  const coords = pontos.map(p => [p.lng, p.lat]);
  coords.push(coords[0]); // fechar anel
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] },
    properties: {},
  };
}
